import { handleApiError, handleApiResponse } from "@/methods/api";
import {
  renumberQuestionSerialsAcrossSections,
  toActivityEventWire,
  toActivitySectionInput,
} from "@/methods/activity";
import ApiVerified from "@/services/apiVerified";
import {
  ActivityAffiliationDisplay,
  ActivityAffiliationReport,
  ActivityAffiliationReportDetails,
  ActivityAllInputs,
  ActivityCreateInputs,
  ActivityData,
  ActivityPublishInputs,
  ActivityReportDispatch,
  ActivityReportSendEligibility,
  ActivityReportSendInputs,
  ActivitySection,
  ActivityStudent,
  ActivityTemplateCreate,
  SaveActivityQuestionsInputs,
  ActivityTemplateDisplay,
} from "@/types/activity";
import { SubjectiveEvaluationStatusDisplay } from "@/types/affiliation";
import {
  ActivityEventView,
  ActivityTimeStamps,
  ConductActivity,
  ConductActivityInput,
  StartActivityInputs,
} from "@/types/conductActivity";
import { ApiResponse, AzureUploadUrl, IdNamePair } from "@/types/global";
import { SelectorQuestion } from "@/types/questionSelector";
import { StudentActivityReport, StudentActivityReportDetails } from "@/types/studentActivityReport";

export default class ActivityApi extends ApiVerified {
  // ──────────────────────────────────────────────
  // 1. Creation & Design
  // ──────────────────────────────────────────────

  async createActivity(activityData: ActivityAllInputs): Promise<ApiResponse> {
    try {
      // AR-7.3: strip each section's Questions to the wire rows (ActivityQuestionCreate) at the
      // save boundary, exactly like save-activity-questions — so create + save send the same shape.
      const wireBody: ActivityCreateInputs = {
        ...activityData,
        Sections: renumberQuestionSerialsAcrossSections(
          activityData.Sections.map(toActivitySectionInput),
        ),
      };
      const response = await this.axiosInstance.post<number>(`activity/create-activity`, wireBody);
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // Same wire shape as create, but PUT edit-activity — the create endpoint rejects a
  // non-null ActivityId (400) for non-cloned activities.
  async editActivity(activityData: ActivityAllInputs): Promise<ApiResponse> {
    try {
      const wireBody: ActivityCreateInputs = {
        ...activityData,
        Sections: renumberQuestionSerialsAcrossSections(
          activityData.Sections.map(toActivitySectionInput),
        ),
      };
      const response = await this.axiosInstance.put<number>(`activity/edit-activity`, wireBody);
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // ──────────────────────────────────────────────
  // 2. Question Selection
  // ──────────────────────────────────────────────

  async getRecommendedQuestions(
    activityId: number,
    sectionInputs: ActivitySection,
    exclusionBatchIds: Array<number>,
    difficultyLevelId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<Array<number>>(
        `activity/get-recommended-questions`,
        {
          ActivityId: activityId,
          // activity.json RecommendedQuestionsInputs takes an `ActivitySectionInput`, so strip the
          // working section the same way the create/save paths do — otherwise the client-only
          // `Subjects` and the view-model `ActivityQuestion` rows ride along.
          SectionInputs: toActivitySectionInput(sectionInputs),
          ExclusionBatchIds: exclusionBatchIds,
          DifficultyLevelId: difficultyLevelId,
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAvailableQuestionsForChapter(
    activityId: number,
    subjectChapterId: number,
    questionTypeIdsCsv: string,
    onlyActive: boolean,
    chapterTopicIdsCsv: string | null = null,
    exclusionBatchIdsCsv: string | null = null,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<SelectorQuestion>>(
        `activity/get-available-questions-for-chapter`,
        {
          params: {
            activityId: activityId,
            subjectChapterId: subjectChapterId,
            questionTypeIdsCsv: questionTypeIdsCsv,
            onlyActive: onlyActive,
            // AR-7.2: doc param is `chapterTopicIdsCsv` (was `topicIdsCsv` — never applied).
            chapterTopicIdsCsv: chapterTopicIdsCsv,
            exclusionBatchIdsCsv: exclusionBatchIdsCsv,
          },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async saveActivityQuestions(
    saveActivityQuestionsInputs: SaveActivityQuestionsInputs,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `activity/save-activity-questions`,
        saveActivityQuestionsInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // ──────────────────────────────────────────────
  // 3. Activity Data
  // ──────────────────────────────────────────────

  async getActivityData(activityId: number, affiliationId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<ActivityData>(`activity/get-activity-data`, {
        params: { activityId: activityId, affiliationId: affiliationId },
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getActivityDataForPreview(activityId: number, affiliationId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<ActivityData>(
        `activity/get-activity-data-for-preview`,
        { params: { activityId: activityId, affiliationId: affiliationId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // ──────────────────────────────────────────────
  // 4. Instructions
  // ──────────────────────────────────────────────

  async getActivityInstructions(activityId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<string>>(
        `activity/get-activity-instructions`,
        { params: { activityId: activityId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async saveActivityInstructions(
    activityId: number,
    affiliationId: number,
    activityInstructions: Array<string>,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `activity/save-activity-instructions`,
        {
          ActivityId: activityId,
          AffiliationId: affiliationId,
          ActivityInstructions: activityInstructions,
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // ──────────────────────────────────────────────
  // 5. Publish / Unpublish / Schedule / Rename / Clone / Delete
  // ──────────────────────────────────────────────

  async publishActivity(activityPublishInputs: ActivityPublishInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `activity/publish-activity`,
        activityPublishInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async unpublishActivity(activityId: number, batchIds: Array<number>): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(`activity/unpublish-activity`, {
        ActivityId: activityId,
        BatchIds: batchIds,
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async rescheduleActivity(
    activityId: number,
    batchIds: Array<number>,
    startDatetime: string,
    endDatetime: string,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(`activity/reschedule-activity`, {
        ActivityId: activityId,
        BatchIds: batchIds,
        StartDatetime: startDatetime,
        EndDatetime: endDatetime,
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async renameActivity(
    activityId: number,
    affiliationId: number,
    activityName: string,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(`activity/rename-activity`, {
        ActivityId: activityId,
        AffiliationId: affiliationId,
        ActivityName: activityName,
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async cloneActivity(
    activityId: number,
    activityName: string,
    affiliationId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(`activity/clone-activity`, {
        ActivityId: activityId,
        ActivityName: activityName,
        AffiliationId: affiliationId,
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async deleteActivity(activityId: number, affiliationId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.delete<boolean>(`activity/delete-activity`, {
        params: {
          activityId: activityId,
          affiliationId: affiliationId,
        },
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // ──────────────────────────────────────────────
  // 6. Listing / Browsing
  // ──────────────────────────────────────────────

  async getActivitiesForAffiliation(
    activityTypeIds: Array<number>,
    activityScopeId: number,
    affiliationId: number,
    category: number,
    pageNumber: number,
    pageSize: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<ActivityAffiliationDisplay>>(
        `activity/get-activities-for-affiliation`,
        {
          params: {
            activityTypeIds: activityTypeIds,
            activityScopeId: activityScopeId,
            affiliationId: affiliationId,
            category: category,
            pageNumber: pageNumber,
            pageSize: pageSize,
          },
        },
      );
      return handleApiResponse(response, true);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getDetailedActivitiesForStudent(
    activityTypeIds: Array<number>,
    activityScopeId: number,
    userId: number,
    category: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<ActivityStudent>>(
        `activity/get-detailed-activities-for-student`,
        {
          params: {
            activityTypeIds: activityTypeIds,
            activityScopeId: activityScopeId,
            userId: userId,
            category: category,
          },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getActivitiesForStudent(
    activityTypeIds: Array<number>,
    userId: number,
    onlyCompleted: boolean,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `activity/get-activities-for-student`,
        {
          params: {
            activityTypeIds: activityTypeIds,
            userId: userId,
            onlyCompleted: onlyCompleted,
          },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getActivitiesForBatch(
    batchId: number,
    activityTypeIds: Array<number>,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `activity/get-activities-for-batch`,
        { params: { batchId: batchId, activityTypeIds: activityTypeIds } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // ──────────────────────────────────────────────
  // 7. Conduct — Load / Start / Events / End
  // ──────────────────────────────────────────────

  async loadActivity(activityId: number, userId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<ConductActivity>(`activity/load-activity`, {
        params: { activityId: activityId, userId: userId },
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async startActivity(startActivityInputs: StartActivityInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<ActivityTimeStamps>(
        `activity/start-activity`,
        startActivityInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async pushActivityEvents(
    activityId: number,
    activityInstanceId: number,
    activityEvents: Array<ActivityEventView>,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(`activity/push-activity-events`, {
        ActivityId: activityId,
        ActivityInstanceId: activityInstanceId,
        // AR-7.4: strip the client-only IsSaved flag from each event before sending.
        ActivityEvents: activityEvents.map(toActivityEventWire),
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async endActivity(conductActivity: ConductActivityInput): Promise<ApiResponse> {
    try {
      // AR-7.4: strip client-only IsSaved from the nested ActivityEvents at the wire boundary.
      const activityState = conductActivity.ActivityState;
      const wireBody = {
        ...conductActivity,
        ActivityState: activityState
          ? { ...activityState, ActivityEvents: activityState.ActivityEvents.map(toActivityEventWire) }
          : activityState,
      };
      const response = await this.axiosInstance.post<boolean>(`activity/end-activity`, wireBody);
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // ──────────────────────────────────────────────
  // 8. Reports
  // ──────────────────────────────────────────────

  async getStudentActivityReport(activityInstanceId: number, userId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<StudentActivityReport>(
        `activity/student-activity-report`,
        { params: { activityInstanceId: activityInstanceId, userId: userId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getStudentActivityReportDetails(
    activityInstanceId: number,
    userId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<StudentActivityReportDetails>(
        `activity/student-activity-report-details`,
        { params: { activityInstanceId: activityInstanceId, userId: userId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAffiliationActivityReport(
    activityId: number,
    affiliationId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<ActivityAffiliationReport>>(
        `activity/affiliation-activity-report`,
        { params: { activityId: activityId, affiliationId: affiliationId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAffiliationActivityReportDetails(
    activityId: number,
    affiliationId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<ActivityAffiliationReportDetails>>(
        `activity/affiliation-activity-report-details`,
        { params: { activityId: activityId, affiliationId: affiliationId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // ──────────────────────────────────────────────
  // 9. Reset
  // ──────────────────────────────────────────────

  async resetStudentActivity(
    activityId: number,
    activityInstanceId: number,
    userId: number,
    affiliationId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.delete<boolean>(`activity/reset-student-activity`, {
        params: {
          activityId: activityId,
          activityInstanceId: activityInstanceId,
          userId: userId,
          affiliationId: affiliationId,
        },
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async resetAllActivityAttempts(
    activityId: number,
    affiliationId: number,
    batchId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.delete<boolean>(
        `activity/reset-all-activity-attempts`,
        {
          params: {
            activityId: activityId,
            affiliationId: affiliationId,
            batchId: batchId,
          },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // ──────────────────────────────────────────────
  // 14. Templates
  // ──────────────────────────────────────────────

  async getSelfActivityTemplates(
    courseId: number,
    activityTypeId: number,
    activityScopeId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<ActivityTemplateDisplay>>(
        `activity/get-self-activity-templates`,
        {
          params: {
            courseId: courseId,
            activityTypeId: activityTypeId,
            activityScopeId: activityScopeId,
          },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getStandardActivityTemplatesForCourse(
    activityTypeId: number,
    courseId: number,
    activityScopeId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<ActivityTemplateDisplay>>(
        `activity/standard-activity-templates-for-course`,
        {
          params: {
            activityTypeId: activityTypeId,
            courseId: courseId,
            activityScopeId: activityScopeId,
          },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getSavedActivityTemplatesForCourse(
    activityTypeId: number,
    courseId: number,
    affiliationId: number,
    activityScopeId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<ActivityTemplateDisplay>>(
        `activity/saved-activity-templates-for-course`,
        {
          params: {
            activityTypeId: activityTypeId,
            courseId: courseId,
            affiliationId: affiliationId,
            activityScopeId: activityScopeId,
          },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async saveActivityTemplate(activityTemplate: ActivityTemplateCreate): Promise<ApiResponse> {
    try {
      // AR-7.1: doc accepts the flat ActivityTemplateCreate body (not `{ ActivityTemplate: … }`).
      const response = await this.axiosInstance.post<boolean>(
        `activity/save-activity-template`,
        activityTemplate,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async deleteActivityTemplate(mongoId: string): Promise<ApiResponse> {
    try {
      // AR-7.1: doc accepts only `mongoId` (dropped activityTypeId/courseId/affiliationId).
      const response = await this.axiosInstance.delete<boolean>(
        `activity/delete-activity-template`,
        { params: { mongoId: mongoId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // ──────────────────────────────────────────────
  // 16. Subjective Answer Upload
  // ──────────────────────────────────────────────

  async getSubjectiveActivityAnswerUploadUrl(
    activityId: number,
    activityInstanceId: number,
    questionId: number,
    contentType: string,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<AzureUploadUrl>(
        `activity/get-subjective-activity-answer-upload-url`,
        {
          params: {
            activityId: activityId,
            activityInstanceId: activityInstanceId,
            questionId: questionId,
            contentType: contentType,
          },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // ──────────────────────────────────────────────
  // 17. Report Generation / Regeneration
  // ──────────────────────────────────────────────

  async saveExistingStudentActivityReport(
    activityId: number,
    userId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `activity/save-student-activity-report`,
        undefined,
        { params: { activityId: activityId, userId: userId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async regenerateStudentActivityReport(
    activityInstanceId: number,
    activityId: number,
    userId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `activity/regenerate-student-activity-report`,
        undefined,
        {
          params: {
            activityInstanceId: activityInstanceId,
            activityId: activityId,
            userId: userId,
          },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async regenerateAffiliationActivityReports(
    activityId: number,
    affiliationId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `activity/regenerate-affiliation-activity-reports`,
        undefined,
        { params: { activityId: activityId, affiliationId: affiliationId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // ──────────────────────────────────────────────
  // 18. Report Notifications (activity-report-notifications router)
  // ──────────────────────────────────────────────

  // GET /v7/activity-report-notifications/activities/{activityId}/batches/{batchId}/eligibility
  // → ActivityReportSendEligibility. A blocked test is a 200 with CanSend:false — only 404
  // (test missing / not scheduled to that batch / another school's, deliberately
  // indistinguishable) is an error. The school comes from the token, so no affiliationId.
  async getActivityReportSendEligibility(
    activityId: number,
    batchId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<ActivityReportSendEligibility>(
        `activity-report-notifications/activities/${activityId}/batches/${batchId}/eligibility`,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // POST /v7/activity-report-notifications/dispatches → 202 ActivityReportDispatch. 202, not
  // 201: the messages are queued and every counter in the response is 0. Every 417 carries a
  // ready-to-show sentence in `detail`, which handleApiError surfaces as errorMessage.
  async sendActivityReport(sendInputs: ActivityReportSendInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<ActivityReportDispatch>(
        `activity-report-notifications/dispatches`,
        sendInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // ──────────────────────────────────────────────
  // 19. Subjective Evaluation Finalization
  // ──────────────────────────────────────────────
  // Teacher-only pair — both authorise as staff of a batch the activity is published to, so a
  // student token gets 401. activityId and batchId are path parameters, not query params.

  // GET /v7/activity/{activityId}/batches/{batchId}/subjective-evaluation/status
  // → SubjectiveEvaluationStatusDisplay.
  async getSubjectiveEvaluationStatus(activityId: number, batchId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<SubjectiveEvaluationStatusDisplay>(
        `activity/${activityId}/batches/${batchId}/subjective-evaluation/status`,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // POST /v7/activity/{activityId}/batches/{batchId}/subjective-evaluation/finalize → 202 with the
  // post-write state; no request body. 202, not 200: the batch flips immediately but the student and
  // batch reports rebuild asynchronously afterwards. Every 417 carries a teacher-readable sentence
  // in `detail`, which handleApiError surfaces as errorMessage.
  async finalizeSubjectiveEvaluation(activityId: number, batchId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<SubjectiveEvaluationStatusDisplay>(
        `activity/${activityId}/batches/${batchId}/subjective-evaluation/finalize`,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }
}
